import * as React from 'react'

import { IntervalReport } from '@/features/ActiveManagement/components/Interval/IntervalReport'
import { BenchmarkingReport } from '@/features/Analytics/components/BenchmarkingReport/BenchmarkingReport'
import { PayablesSummaryTableContainer } from '@/features/BillingPayments/containers/PayablesSummaryTableContainer/PayablesSummaryTableContainer'
import { mapParamsToFilters } from '@/features/Reporting/components/ReportEdit/mapParamsToFilters'
import { processBenchmarkingFilters } from '@/features/Reporting/utils/processBenchmarkingFilters'
import { processPayablesFilters } from '@/features/Reporting/utils/processPayablesFilters'
import { processUsageCostFilters } from '@/features/Reporting/utils/processUsageCostFilters'
import { GHGEmissionsReport } from '@/features/Sustainability/components/GHGEmissions/GHGEmissionsReport'
import { TotalEmissionsReport } from '@/features/Sustainability/components/TotalEmissions/TotalEmissionsReport'
import useSustainabilityStore from '@/features/Sustainability/stores/useSustainabilityStore/useSustainabilityStore'
import {
    BenchmarkingReportSettingsParams,
    CustomReportFiltersParams,
    GetPayablesSummaryRequestParams,
    GHGEmissionsReportSettings,
    IntervalReportSettings,
    ReportType,
    TotalEmissionsReportSettings,
} from '@/hooks'
import { DEFAULT_BENCHMARKING_REPORT_SETTINGS } from '@/utils/constants'
import { mapBenchmarkingParamsToReportSettings } from '../../../../components/data/Benchmarking/mapBenchmarkingParamsToReportSettings'
import { UsageAndCostReport } from '../../../../components/data/UsageAndCost/UsageAndCostReport/UsageAndCostReport'

type ReportWithDrawerProps = {
    type: ReportType
    filters: CustomReportFiltersParams
    settings?:
        | BenchmarkingReportSettingsParams
        | TotalEmissionsReportSettings
        | GHGEmissionsReportSettings
        | IntervalReportSettings
    dataSets: string[]
    isOpen?: boolean
    calculationMethodOffset?: number
}

export const ReportWithDrawer: React.FC<ReportWithDrawerProps> = (props) => {
    const {
        filters,
        type,
        settings,
        isOpen,
        dataSets,
        calculationMethodOffset,
    } = props

    const {
        totalEmissions: {
            selectedView: totalEmissionsView,
            setSelectedView: setTotalEmissionsView,
        },
        electricityMix: {
            selectedView: electricityMixView,
            setSelectedView: setElectricityMixView,
        },
    } = useSustainabilityStore((state) => state)

    if (type === 'usage-cost') {
        const usageCostFilters = processUsageCostFilters(filters)

        return (
            <UsageAndCostReport
                filters={usageCostFilters}
                dataSets={dataSets}
                isOpen={isOpen}
                calculationMethodOffset={calculationMethodOffset}
            />
        )
    }

    if (type === 'payables-summary') {
        const payablesFilters: GetPayablesSummaryRequestParams =
            processPayablesFilters(filters)

        return (
            <div className="h-full w-full overflow-auto p-6">
                <PayablesSummaryTableContainer filters={payablesFilters} />
            </div>
        )
    }

    if (type === 'total-emissions') {
        const reportSettingsValues = settings as TotalEmissionsReportSettings
        const view =
            reportSettingsValues.type === 'electricitySources'
                ? electricityMixView
                : totalEmissionsView
        const setView =
            reportSettingsValues.type === 'electricitySources'
                ? setElectricityMixView
                : setTotalEmissionsView

        return (
            <TotalEmissionsReport
                reportSettings={reportSettingsValues}
                filters={mapParamsToFilters(filters)}
                view={view}
                setView={setView}
            />
        )
    }

    if (type === 'ghg-emissions') {
        const reportSettingsValues = settings as GHGEmissionsReportSettings

        return (
            <GHGEmissionsReport
                reportSettings={reportSettingsValues}
                filters={mapParamsToFilters(filters)}
            />
        )
    }

    if (type === 'interval-data') {
        const reportSettingsValues = settings as IntervalReportSettings

        const displayValue: 'subMeters' | 'meters' =
            reportSettingsValues.type === 'subMeterData'
                ? 'subMeters'
                : 'meters'

        const reconstructedSettings: IntervalReportSettings = {
            ...reportSettingsValues,
            display: displayValue,
            showHighlights: reportSettingsValues.showHighlights,
            granularity: reportSettingsValues.granularity,
        }

        return (
            <IntervalReport
                reportSettings={reconstructedSettings}
                activeFilters={mapParamsToFilters(filters)}
                isOpen={isOpen}
                calculationMethodOffset={calculationMethodOffset}
            />
        )
    }

    const benchmarkingParams = processBenchmarkingFilters(filters)
    const benchmarkingSettings =
        props.settings && 'basis' in props.settings ? props.settings : undefined

    const reportSettings = {
        ...mapBenchmarkingParamsToReportSettings(benchmarkingParams),
        basis:
            benchmarkingSettings?.basis ??
            DEFAULT_BENCHMARKING_REPORT_SETTINGS.basis,
    }

    return (
        <BenchmarkingReport
            filters={benchmarkingParams}
            reportSettings={reportSettings}
            isOpen={isOpen}
            calculationMethodOffset={calculationMethodOffset}
        />
    )
}
